import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faLeaf, faChevronDown } from '@fortawesome/free-solid-svg-icons';

// Constants
const NAV_ITEMS = [
  { name: 'Home', path: '/', icon: '🏠' },
  { 
    name: 'About Us', 
    path: '/about', 
    icon: '👥'
  },
  { 
    name: 'Services', 
    path: '/services', 
    icon: '⚡',
    dropdown: [
      { name: 'Solar Power', path: '/services/solar' },
      { name: 'Wind Energy', path: '/services/wind' },
      { name: 'Hydroelectric', path: '/services/hydro' }, 
      { name: 'All Services', path: '/services' },
    ]
  },
  { 
    name: 'Blog', 
    path: '/blog', 
    icon: '📝'
  },
  { name: 'Contact', path: '/contact', icon: '📞' }
];

// Custom hook for dropdown management
const useDropdown = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const openDropdown = (dropdownName) => {
    setActiveDropdown(dropdownName);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  const toggleDropdown = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [activeDropdown]);

  return {
    activeDropdown,
    openDropdown,
    closeDropdown,
    toggleDropdown,
    dropdownRef
  };
};

// Custom hook for scroll detection
const useScrollDetection = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return scrolled;
};

// Custom hook for body scroll lock
const useBodyScrollLock = (isLocked) => {
  useEffect(() => {
    console.log('Body scroll lock:', isLocked);
    if (isLocked) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      console.log('Body locked with styles:', {
        position: document.body.style.position,
        top: document.body.style.top,
        overflow: document.body.style.overflow
      });
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
      console.log('Body unlocked, styles cleared');
    }
    
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      console.log('Body scroll lock cleanup');
    };
  }, [isLocked]);
};

// Components
const Logo = ({ onLogoClick }) => (
  <Link 
    to="/" 
    className="flex items-center gap-3 group"
    onClick={onLogoClick}
  >
    <div className="relative h-24 lg:h-24 w-56 lg:w-64">
      <img 
        src="/images/image.png" 
        alt="Corenova Logo" 
        className="h-full w-full object-cover transition-all duration-300 group-hover:scale-105 drop-shadow-lg"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-green/30 to-secondary-blue/30 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  </Link>
);

const MobileMenuButton = ({ isOpen, onClick }) => (
  <button 
    className="lg:hidden relative p-4 rounded-2xl bg-gradient-to-r from-primary-green to-secondary-blue hover:from-primary-green/90 hover:to-secondary-blue/90 transition-all duration-300 group shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary-green/50"
    onClick={onClick}
    aria-label="Toggle menu"
    style={{ touchAction: 'manipulation' }}
  >
    <div className="relative">
      <FontAwesomeIcon 
        icon={isOpen ? faTimes : faBars} 
        className={`text-2xl text-white transition-all duration-300 ${
          isOpen ? 'rotate-90' : ''
        }`}
      />
      <div className="absolute inset-0 bg-white/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  </button>
);

const DropdownMenu = ({ 
  item, 
  isActive, 
  activeDropdown, 
  toggleDropdown, 
  closeDropdown, 
  dropdownRef 
}) => (
  <div className="relative group" ref={dropdownRef}>
    <button
      onClick={() => toggleDropdown(item.name)}
      onMouseEnter={() => {
        // Optional: Open on hover for desktop
        // toggleDropdown(item.name);
      }}
      className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all duration-300 ${
        isActive 
          ? 'text-primary-green bg-primary-green/15 shadow-lg' 
          : 'text-dark hover:text-primary-green hover:bg-primary-green/10 hover:shadow-md'
      }`}
    >
      <span className="text-lg">{item.icon}</span>
      {item.name}
      <FontAwesomeIcon 
        icon={faChevronDown} 
        className={`text-xs transition-transform duration-300 ${
          activeDropdown === item.name ? 'rotate-180' : ''
        }`}
      />
    </button>
    
    <div 
      className={`absolute top-full left-0 mt-2 w-64 bg-white/98 backdrop-blur-lg rounded-2xl shadow-2xl border border-gray-100/50 transition-all duration-300 transform origin-top ${
        activeDropdown === item.name 
          ? 'opacity-100 visible scale-100 translate-y-0' 
          : 'opacity-0 invisible scale-95 -translate-y-2 pointer-events-none'
      }`}
      onMouseLeave={closeDropdown}
    >
      <div className="p-2">
        {item.dropdown.map((dropdownItem) => (
          <Link
            key={dropdownItem.name}
            to={dropdownItem.path}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-dark hover:text-primary-green hover:bg-primary-green/10 transition-all duration-300 group/item"
            onClick={(e) => {
              console.log(`Dropdown click: ${dropdownItem.name} -> ${dropdownItem.path}`);
              closeDropdown();
            }}
          >
            <div className="w-2 h-2 bg-primary-green rounded-full opacity-0 group-hover/item:opacity-100 transition-opacity duration-300" />
            {dropdownItem.name}
          </Link>
        ))}
      </div>
    </div>
  </div>
);

const NavItem = ({ item, isActive, activeDropdown, toggleDropdown, closeDropdown, dropdownRef }) => {
  if (item.dropdown) {
    return (
      <DropdownMenu
        item={item}
        isActive={isActive}
        activeDropdown={activeDropdown}
        toggleDropdown={toggleDropdown}
        closeDropdown={closeDropdown}
        dropdownRef={dropdownRef}
      />
    );
  }

  return (
    <Link 
      to={item.path} 
      onClick={(e) => {
        console.log(`Navbar click: ${item.name} -> ${item.path}`);
        // Don't prevent default - let React Router handle it
      }}
      className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all duration-300 ${
        isActive 
          ? 'text-primary-green bg-primary-green/15 shadow-lg' 
          : 'text-dark hover:text-primary-green hover:bg-primary-green/10 hover:shadow-md'
      }`}
    >
      <span className="text-lg">{item.icon}</span>
      {item.name}
    </Link>
  );
};

const DesktopNavigation = ({ activeDropdown, toggleDropdown, closeDropdown, dropdownRef, isActive }) => (
  <div className="hidden lg:flex lg:items-center lg:gap-2">
    {NAV_ITEMS.map((item) => (
      <NavItem
        key={item.name}
        item={item}
        isActive={isActive(item.path)}
        activeDropdown={activeDropdown}
        toggleDropdown={toggleDropdown}
        closeDropdown={closeDropdown}
        dropdownRef={dropdownRef}
      />
    ))}
  </div>
);

const MobileDropdownItem = ({ item, activeDropdown, toggleDropdown, closeMenu, isActive }) => (
  <div className="space-y-2">
    <button
      onClick={() => toggleDropdown(item.name)}
      className={`w-full flex items-center justify-between p-5 rounded-2xl font-semibold text-lg transition-all duration-300 shadow-md hover:shadow-lg ${
        isActive 
          ? 'text-white bg-gradient-to-r from-primary-green to-secondary-blue' 
          : 'text-dark bg-white hover:bg-gradient-to-r hover:from-primary-green/10 hover:to-secondary-blue/10'
      }`}
      style={{ touchAction: 'manipulation' }}
    >
      <div className="flex items-center gap-4">
        <span className="text-2xl">{item.icon}</span>
        <span className="font-bold">{item.name}</span>
      </div>
      <FontAwesomeIcon 
        icon={faChevronDown} 
        className={`text-lg transition-transform duration-300 ${
          activeDropdown === item.name ? 'rotate-180' : ''
        }`}
      />
    </button>
    
    <div className={`ml-8 space-y-2 overflow-hidden transition-all duration-500 ${
      activeDropdown === item.name ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
    }`}>
      {item.dropdown.map((dropdownItem, idx) => (
        <Link
          key={dropdownItem.name}
          to={dropdownItem.path}
          className="flex items-center gap-4 p-4 rounded-xl text-gray-700 hover:text-primary-green hover:bg-gradient-to-r hover:from-primary-green/5 hover:to-secondary-blue/5 transition-all duration-300 bg-white shadow-sm hover:shadow-md"
          onClick={closeMenu}
          style={{ touchAction: 'manipulation' }}
        >
          <div className="w-3 h-3 bg-gradient-to-r from-primary-green to-secondary-blue rounded-full" />
          <span className="font-medium">{dropdownItem.name}</span>
        </Link>
      ))}
    </div>
  </div>
);

const MobileNavItem = ({ item, activeDropdown, toggleDropdown, closeMenu, isActive, index }) => {
  if (item.dropdown) {
    return (
      <MobileDropdownItem
        item={item}
        activeDropdown={activeDropdown}
        toggleDropdown={toggleDropdown}
        closeMenu={closeMenu}
        isActive={isActive}
      />
    );
  }

  return (
    <Link 
      to={item.path} 
      className={`flex items-center gap-4 p-5 rounded-2xl font-semibold text-lg transition-all duration-300 shadow-md hover:shadow-lg ${
        isActive 
          ? 'text-white bg-gradient-to-r from-primary-green to-secondary-blue' 
          : 'text-dark bg-white hover:bg-gradient-to-r hover:from-primary-green/10 hover:to-secondary-blue/10'
      }`}
      onClick={closeMenu}
      style={{ touchAction: 'manipulation' }}
    >
      <span className="text-2xl">{item.icon}</span>
      <span className="font-bold">{item.name}</span>
    </Link>
  );
};

const MobileMenu = ({ isOpen, closeMenu, activeDropdown, toggleDropdown, isActive }) => (
  <div 
    className={`lg:hidden fixed top-0 left-0 w-full h-screen bg-gradient-to-br from-white via-gray-50 to-white z-[70] transition-transform duration-500 ease-in-out ${
      isOpen ? 'translate-x-0' : '-translate-x-full'
    }`}
    style={{ touchAction: 'none' }}
  >
    <button
      onClick={closeMenu}
      aria-label="Close menu"
      className="absolute top-4 right-4 z-[80] p-3 rounded-full bg-white shadow-lg hover:shadow-xl text-dark hover:text-primary-green transition-colors"
      style={{ touchAction: 'manipulation' }}
    >
      <FontAwesomeIcon icon={faTimes} className="text-xl" />
    </button>

    <div className="pt-24 px-4 py-8 h-full overflow-y-auto" style={{ touchAction: 'pan-y' }}>
      <div className="space-y-3">
        {NAV_ITEMS.map((item, index) => (
          <div key={item.name} className="animate-fade-in-up" style={{ animationDelay: `${index * 100}ms` }}>
            <MobileNavItem
              item={item}
              activeDropdown={activeDropdown}
              toggleDropdown={toggleDropdown}
              closeMenu={closeMenu}
              isActive={isActive(item.path)}
              index={index}
            />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Main Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  
  // Custom hooks
  const scrolled = useScrollDetection();
  const { 
    activeDropdown, 
    openDropdown, 
    closeDropdown, 
    toggleDropdown, 
    dropdownRef 
  } = useDropdown();
  
  // Lock body scroll when mobile menu is open
  useBodyScrollLock(isOpen);

  // Close mobile menu and dropdown when route changes
  useEffect(() => {
    setIsOpen(false);
    closeDropdown();
  }, [location.pathname]);

  const toggleMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
    // Close dropdown when opening mobile menu
    closeDropdown();
  };

  const closeMenu = () => {
    setIsOpen(false);
    closeDropdown();
  };

  const handleLogoClick = () => {
    closeMenu();
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[65] lg:hidden"
          onClick={closeMenu}
          style={{ touchAction: 'none' }}
        />
      )}

      <nav className={`fixed top-0 w-full z-[70] transition-all duration-500 ease-in-out ${
        scrolled 
          ? 'bg-white shadow-2xl border-b-2 border-primary-green/20' 
          : 'bg-white/95 backdrop-blur-lg border-b border-gray-200/50'
      }`}>
        <div className="container">
          <div className="flex justify-between items-center h-24 lg:h-24">
            <Logo onLogoClick={handleLogoClick} />
            
            <DesktopNavigation
              activeDropdown={activeDropdown}
              toggleDropdown={toggleDropdown}
              closeDropdown={closeDropdown}
              dropdownRef={dropdownRef}
              isActive={isActive}
            />

            <MobileMenuButton isOpen={isOpen} onClick={toggleMenu} />
          </div>
        </div>

        <MobileMenu
          isOpen={isOpen}
          closeMenu={closeMenu}
          activeDropdown={activeDropdown}
          toggleDropdown={toggleDropdown}
          isActive={isActive}
        />
      </nav>
    </>
  );
};

export default Navbar;